import {
  clearFilesSession,
  installFilesImageRetry,
  issueFilesSession
} from "~/utils/files-cdn"

export const FILES_SESSION_REFRESH_MS = 21600000
export const FILES_SESSION_STALE_MS = 18000000
const WAKE_EVENTS = ["visibilitychange", "focus", "online"] as const

type Ctx = {
  token: string
  tenantId: number
  userId: number
  linkedTenantId?: number | null
}

type Deps = {
  issue?: (c: Ctx) => Promise<void>
  install?: (refresh: () => Promise<void>) => () => void
  setInterval?: typeof setInterval
  clearInterval?: typeof clearInterval
  now?: () => number
  wakeTarget?: Pick<
    EventTarget,
    "addEventListener" | "removeEventListener"
  > | null
}

export function startFilesSession(getCtx: () => Ctx | null, deps: Deps = {}) {
  const issue = deps.issue ?? ((c: Ctx) => issueFilesSession(c))
  const install = deps.install ?? installFilesImageRetry
  const setI = deps.setInterval ?? setInterval
  const clearI = deps.clearInterval ?? clearInterval
  const now = deps.now ?? Date.now
  const wake =
    deps.wakeTarget === undefined
      ? typeof window === "undefined"
        ? null
        : window
      : deps.wakeTarget
  const first = getCtx()
  if (!first) return () => {}
  let issuedAt = now()
  const refresh = async () => {
    const c = getCtx()
    if (!c) return
    issuedAt = now()
    await issue(c)
  }
  const onWake = () => {
    if (now() - issuedAt >= FILES_SESSION_STALE_MS) void refresh()
  }
  void issue(first)
  const uninstall = install(refresh)
  const timer = setI(() => {
    void refresh()
  }, FILES_SESSION_REFRESH_MS)
  WAKE_EVENTS.forEach((e) => wake?.addEventListener(e, onWake))
  return () => {
    clearI(timer)
    uninstall()
    WAKE_EVENTS.forEach((e) => wake?.removeEventListener(e, onWake))
  }
}

export async function clearFilesSessionBounded(ctx: Ctx | null, ms = 1500) {
  if (!ctx) return
  await Promise.race([
    clearFilesSession(ctx),
    new Promise((r) => setTimeout(r, ms))
  ])
}
